import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import FamilyShell from "../../components/family/FamilyShell";

const API = process.env.REACT_APP_API_URL;

const TABS = [
  { key: "profil", label: "Profil" },
  { key: "kalender", label: "Kalender" },
  { key: "erinnerungen", label: "Erinnerungen" },
  { key: "feed", label: "Feed" },
];

function PlaceholderCard({ title, text }) {
  return (
    <div className="rounded-3xl border border-black/10 bg-white/80 p-6 shadow-sm">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <p className="mt-2 text-sm text-gray-600">{text}</p>
    </div>
  );
}

export default function FamilyPatientPage() {
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const activeTab = TABS.some((item) => item.key === tab) ? tab : "profil";
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/family/patients/${id}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) throw new Error(data?.error || "Profil konnte nicht geladen werden.");
        setPatient(data);
      } catch (error) {
        if (!alive) return;
        setErr(error?.message || "Profil konnte nicht geladen werden.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const fullName = useMemo(() => {
    if (!patient) return "Bewohner";
    return `${patient.forename} ${patient.surname}`;
  }, [patient]);

  return (
    <FamilyShell title={loading ? "Bewohner" : fullName} backTo="/family">
      <div className="mb-6 flex flex-wrap gap-3">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => navigate(item.key === "profil" ? `/family/patients/${id}` : `/family/patients/${id}/${item.key}`)}
            className={[
              "rounded-full px-4 py-2 text-sm font-medium",
              activeTab === item.key
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 shadow-sm hover:bg-gray-50",
            ].join(" ")}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? <p className="text-sm text-gray-600">Profil wird geladen...</p> : null}
      {err ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {err}
        </div>
      ) : null}

      {!loading && !err && patient && activeTab === "profil" ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr,0.9fr]">
          <div className="rounded-3xl border border-black/10 bg-white/80 p-6 shadow-sm">
            <p className="text-sm font-medium text-blue-700">Basisprofil</p>
            <h2 className="mt-2 text-2xl font-semibold text-gray-900">
              {patient.forename} {patient.surname}
            </h2>
          </div>
          <PlaceholderCard
            title="Familienbereich"
            text="Hier werden spaeter freigegebene Informationen fuer Angehoerige angezeigt."
          />
        </div>
      ) : null}

      {!loading && !err && patient && activeTab === "kalender" ? (
        <PlaceholderCard
          title="Kalender"
          text="Die Familienansicht fuer Termine und Kalender folgt im naechsten Schritt."
        />
      ) : null}

      {!loading && !err && patient && activeTab === "erinnerungen" ? (
        <PlaceholderCard
          title="Erinnerungen"
          text="Die familiengeeignete Erinnerungsansicht wird hier als naechstes angeschlossen."
        />
      ) : null}

      {!loading && !err && patient && activeTab === "feed" ? (
        <PlaceholderCard
          title="Familien-Feed"
          text="Hier entsteht spaeter der Bereich fuer Fotos, Videos und Familienbeitraege."
        />
      ) : null}

      {!loading && !err ? (
        <div className="mt-6">
          <Link to="/family" className="text-sm font-medium text-blue-700 hover:text-blue-800">
            Zurueck zur Uebersicht
          </Link>
        </div>
      ) : null}
    </FamilyShell>
  );
}
